import { useConnect } from '@arcadia-eng/connect-react';
import { useEffect, PropsWithChildren } from 'react';
import { AmerenSignup } from '~/components/ameren-signup';
import { Button } from '~/components/button';
import { ErrorMessage } from '~/components/error-message';
import { UtilityConnectOnClose } from '~/typings/utility-connect';
import { UTILITY_CONNECT_ENV, UTILITY_IDS } from '~/utils/constants';
import { Status, Logger, EventName } from '~/utils/logger';
import { trackError } from '~/utils/tracking';

type Props = PropsWithChildren<{
  error?: string;
  onClose: UtilityConnectOnClose;
  timeout?: number;
  utilityConnectToken?: string;
  utilityId?: number;
  onError?: () => void;
}>;

export const UtilityConnectButton = ({
  children,
  error,
  onClose,
  timeout = 30000,
  utilityConnectToken,
  utilityId,
  onError,
}: Props) => {
  const [widgetStatus, openWidget] = useConnect();

  const config = {
    _experimental: {
      cannotChangeUtility: true,
      language: window.Localize?.getLanguage(),
      showArcadiaSupport: true,
    },
    callbacks: { onClose }, // TODO: do we want an onError handler?
    env: UTILITY_CONNECT_ENV,
    connectToken: utilityConnectToken,
    poll: { timeout },
  };

  useEffect(() => {
    if (widgetStatus.error) {
      onError?.();
      const modifiedConfig = {
        ...config,
        connectToken: !!utilityConnectToken,
      };
      Logger.logEvent(
        {
          status: Status.Failed,
          error: widgetStatus.error,
          endTime: Date.now(),
          ...modifiedConfig,
        },
        EventName.UtilityConnectService,
      );
      trackError(widgetStatus.error);
    }
  }, [widgetStatus.error]);

  if (UTILITY_IDS['Ameren IL'] === utilityId || UTILITY_IDS['Ameren MO'] === utilityId) {
    return <AmerenSignup error={error} />;
  } else {
    // TODO: rebase and update button cy prop
    return (
      <>
        <ErrorMessage mt={2} error={error || widgetStatus.error} style={{ marginBottom: '32px' }} />
        <Button
          loading={widgetStatus.loading || !utilityConnectToken}
          mt={0}
          mode="primary"
          onClick={() => {
            const modifiedConfig = {
              ...config,
              connectToken: !!utilityConnectToken,
            };
            Logger.logEvent(
              {
                status: Status.Started,
                ...modifiedConfig,
                startTime: Date.now(),
              },
              EventName.UtilityConnectService,
            );
            openWidget(config);
          }}
          testId="sync-utility-button"
        >
          {children}
        </Button>
      </>
    );
  }
};
